/**
 * RadarChart — Spider/radar chart wrapper
 */

import { lazy, Suspense, useMemo } from "react";
import { getChartTheme, useThemeVersion } from "./chart-theme";

const ReactECharts = lazy(() => import("echarts-for-react"));

interface RadarChartProps {
  indicators: { name: string; max: number }[];
  values: number[];
  color?: string;
  height?: number;
}

export function RadarChart({
  indicators,
  values,
  color,
  height = 250,
}: RadarChartProps) {
  const themeVersion = useThemeVersion();
  const option = useMemo(() => {
    const theme = getChartTheme();
    const chartColor = color ?? theme.primary;

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        backgroundColor: "rgba(0,0,0,0.8)",
        borderColor: "transparent",
        textStyle: { color: "#fff", fontSize: 12 },
      },
      radar: {
        indicator: indicators,
        shape: "polygon" as const,
        axisName: {
          color: theme.mutedForeground,
          fontSize: 10,
        },
        splitLine: { lineStyle: { color: theme.border, opacity: 0.3 } },
        splitArea: { show: false },
        axisLine: { lineStyle: { color: theme.border, opacity: 0.3 } },
      },
      series: [
        {
          type: "radar" as const,
          data: [
            {
              value: values,
              areaStyle: { opacity: 0.15 },
              lineStyle: { width: 2, color: chartColor },
              itemStyle: { color: chartColor },
            },
          ],
          animationDuration: 800,
        },
      ],
    };
  }, [indicators, values, color, height, themeVersion]);

  return (
    <Suspense fallback={<div style={{ height: `${height}px` }} />}>
      <ReactECharts
        option={option}
        style={{ height: `${height}px`, width: "100%" }}
        opts={{ renderer: "canvas" }}
      />
    </Suspense>
  );
}
